import Wizard from '@service-victoria/component-library/components/Wizard';
import Props from '@service-victoria/component-library/props';
import Utils from '@service-victoria/component-library/utils';
import { BroadcastChannel } from 'broadcast-channel';
import { FormikErrors, FormikTouched } from 'formik';
import * as React from 'react';
import { useLocation } from 'react-router-dom';
import * as Yup from 'yup';
import { v4 as uuid } from 'uuid';

import { app as appContext } from 'app/context';
import useAvailable from 'app/hooks/useAvailable';
import * as Interfaces from 'app/interfaces';
import * as Steps from '../../../transactions/redemption/steps';
import { constants, storage, logger } from 'app/utils';
import { TransactionType } from 'app/interfaces/common';
import { userManager } from 'app/services';
import { individualSchema } from 'app/transactions/redemption/steps/Redeem/Individual';
import { bulkSchema } from 'app/transactions/redemption/steps/Redeem/Bulk';

const channel = new BroadcastChannel<
  Pick<Interfaces.Store.State['transactions'], TransactionType.Redemption>
>(constants.REDEMPTION_BROADCAST_CHANNEL_KEY, {
  webWorkerSupport: false
});

const Redemption: React.FC = () => {
  // The wizard was created with "hash" url support, so we need to extract its value to know at what step the user was
  const location = useLocation();
  const context = appContext.useAppContext();
  const { hash } = location;

  // Redirect user back to home page if program is outside redemption period
  useAvailable();

  React.useEffect(() => {
    const onTransactionComplete = (
      event: Pick<Interfaces.Store.State['transactions'], TransactionType.Redemption>
    ) => {
      if (
        event[TransactionType.Redemption].ID ===
        context.currentState.transactions[TransactionType.Redemption].ID
      ) {
        const newTransactionId = uuid();
        // Another tab has completed with the same transaction ID. Set a new one.
        context.actions.setTransactionID(newTransactionId, TransactionType.Redemption);
      }
    };

    channel.addEventListener('message', onTransactionComplete);
    return () => {
      channel.removeEventListener('message', onTransactionComplete);
    };
  }, [context.actions, context.currentState.transactions]);

  // Get the current hash value of the url to set initialStep index (to load the user in same step in case the screen got refershed in middle of the process)
  // const initialStepId = hash.substr(1) ? hash.substr(1) : Steps.Step1.Step.id;
  const initialStepId = hash.substr(1) ? hash.substr(1) : Steps.Redeem.Select.Step.id;

  const steps: Props.Wizard.Step<any>[] = [
    // Redeem or validate
    Steps.Redeem.Select.Step,
    Steps.Redeem.Individual.Step,
    Steps.Redeem.Bulk.Step,
    // Review
    Steps.Review.Step,
    // Done
    Steps.Done.Step,
    // The below Error step will not appear in form stepper, as it shares the same step name as the Done step
    Steps.Error.Step
  ];

  /**
   * Try to load existing form state from the browser storage in case of user refresh
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const initialState = storage.getStorage<Interfaces.Common.RedemptionFormValues>(
    constants.RedemptionStorageKeys.Wizard
  );

  const initialValues: Interfaces.Common.RedemptionFormValues = {
    redeem: initialState.redeem ?? {
      ...Steps.Redeem.Select.initialValues.redeem,
      ...Steps.Redeem.Individual.initialValues.redeem,
      ...Steps.Redeem.Bulk.initialValues.redeem
    },
    review: initialState.review ?? Steps.Review.initialValues.review
  };

  const initialErrors: FormikErrors<Interfaces.Common.RedemptionFormValues> = {
    ...Steps.Redeem.Select.initialErrors,
    ...Steps.Redeem.Individual.initialErrors,
    ...Steps.Redeem.Bulk.initialErrors,
    ...Steps.Review.initialErrors
  };

  const initialTouched: FormikTouched<Interfaces.Common.RedemptionFormValues> = {
    ...Steps.Redeem.Select.initialTouched,
    ...Steps.Redeem.Individual.initialTouched,
    ...Steps.Redeem.Bulk.initialTouched,
    ...Steps.Review.initialTouched
  };

  const validationSchema = Yup.object<Interfaces.Common.RedemptionFormValues>().shape({
    redeem: Yup.object({
      redemptionType: Steps.Redeem.Select.redeemSchema.fields.redemptionType,
      individual: Yup.mixed().when('redemptionType', {
        is: (type: Interfaces.Common.RedemptionType) =>
          type == Interfaces.Common.RedemptionType.Individual,
        then: () => individualSchema,
        otherwise: () => Yup.mixed().notRequired()
      }),
      bulk: Yup.mixed().when('redemptionType', {
        is: (type: Interfaces.Common.RedemptionType) =>
          type == Interfaces.Common.RedemptionType.Bulk,
        then: () => bulkSchema,
        otherwise: () => Yup.mixed().notRequired()
      })
    }),
    review: Steps.Review.reviewSchema
  });

  const wizardStorage: Props.Wizard.Storage<Interfaces.Common.RedemptionFormValues> = {
    key: constants.RedemptionStorageKeys.Wizard,
    loadOnMount: true,
    ...Utils.storage(sessionStorage)
  };

  const initialAppStates: Props.Wizard.AppContextStates = {
    [constants.RedemptionStorageKeys.State]: context
  };

  React.useEffect(() => {
    logger.setContext('transactionId', context.currentState.transactionID);
  }, [context.currentState.transactionID]);

  return (
    <Wizard
      headerProps={{
        ...constants.headerProps,
        ...userManager.signinHeaderProps
      }}
      initialValues={initialValues}
      initialTouched={initialTouched}
      initialErrors={initialErrors}
      initialAppStates={initialAppStates}
      validationSchema={validationSchema}
      storage={wizardStorage}
      steps={steps}
      initialStepId={initialStepId}
      breadCrumbs={constants.breadCrumbs}
      onSubmit={() => {
        const { ID, startTs } = context.currentState.transactions[TransactionType.Redemption];

        if (!channel.isClosed) {
          channel.postMessage({
            [TransactionType.Redemption]: { ID, startTs }
          });
          channel.close();
        }

        logger.info('Complete transaction', {
          transactionID: ID,
          executionTime: startTs ? +new Date() - startTs : 0
        });
      }}
      isStepperEnabed
      isHashEnabled
      showFooter
      scrollOnSubmitError
    />
  );
};

export default Redemption;
